<script setup>
import { Head } from '@inertiajs/vue3'
import Button from '@/Components/Button.vue'
import { ref, onMounted } from 'vue'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const headerTitleRef = ref(null)
const headerSubtitleRef = ref(null)
const heroRef = ref(null)
const heroGridRef = ref(null)
const contentContainerRef = ref(null)
const sectionHeaders = [
    'Acceptatie van voorwaarden',
    'Diensten',
    'Intellectuele eigendom',
    'Gebruiksvoorwaarden',
    'Beperking van aansprakelijkheid',
    'Wijzigingen',
    'Toepasselijk recht'
]

onMounted(() => {
    // Fade out header grid on scroll
    gsap.to(heroGridRef.value, {
        opacity: 0, 
        y: -50,
        scrollTrigger: {
            trigger: heroRef.value,
            start: 'top top',
            end: '+=300',
            scrub: true
        }
    });

    // Header animations
    gsap.fromTo(headerTitleRef.value, 
        { y: 60, opacity: 0 }, 
        { y: 0, opacity: 1, duration: 1.2, ease: 'power3.out' }
    );
    
    gsap.fromTo(headerSubtitleRef.value, 
        { y: 30, opacity: 0 }, 
        { y: 0, opacity: 1, duration: 1, delay: 0.4, ease: 'power2.out' }
    );
    
    // Content container animation - same as Privacy page
    gsap.fromTo(contentContainerRef.value, 
        { y: 40, opacity: 0 }, 
        { y: 0, opacity: 1, duration: 1.2, delay: 0.8, ease: 'power2.out' }
    );
})
</script>

<template>    
    <main>
        <div ref="heroGridRef" class="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f2e_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f2e_1px,transparent_1px)] bg-[size:54px_54px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />

        <div ref="heroRef" class="wide-container pt-32 md:pt-48 relative z-10">
            <div class="mx-auto max-w-3xl text-center">
                <h1 ref="headerTitleRef" class="text-pretty text-4xl font-semibold text-zinc-900 sm:text-5xl lg:text-6xl max-w-4xl tracking-tight leading-[120%]">Algemene Voorwaarden</h1>
                <p ref="headerSubtitleRef" class="text-balance text-xl text-zinc-600 max-w-2xl mx-auto mt-6">De juridische voorwaarden die gelden voor het gebruik van onze diensten.</p>
            </div>
        </div>
        
        <div class="wide-container">
            <div ref="contentContainerRef" class="max-w-screen-lg mx-auto text-zinc-600 space-y-16 -mb-16 sm:-mb-20 md:-mb-28">
                <!-- Section 1 -->
                <section class="prose prose-lg max-w-none transition duration-700 p-2">
                    <h2 class="text-3xl font-medium text-zinc-900 mb-4">1. Acceptatie van voorwaarden</h2>
                    <p>Door gebruik te maken van de diensten van <span class="text-nowrap">c-o-d-e-s</span> software development, accepteer je de volgende algemene voorwaarden. Als je niet akkoord gaat met deze voorwaarden, vragen wij je geen gebruik te maken van onze diensten.</p>
                    <p>Deze voorwaarden vormen een bindende overeenkomst tussen jou en <span class="text-nowrap">c-o-d-e-s</span> software development.</p>
                </section>
                
                <!-- Section 2 -->
                <section class="prose prose-lg max-w-none transition duration-700 p-2">
                    <h2 class="text-3xl font-medium text-zinc-900 mb-4">2. Diensten</h2>
                    <p><span class="text-nowrap">c-o-d-e-s</span> ontwikkelt en levert custom software oplossingen, platforms en gerelateerde diensten. De exacte omvang en specificaties van de diensten worden vastgelegd in een afzonderlijke overeenkomst of offerte.</p>
                    <p>We streven ernaar om alle diensten met de hoogste kwaliteit en volgens de overeengekomen specificaties te leveren. We behouden ons echter het recht voor om wijzigingen aan te brengen in de diensten als dit noodzakelijk is voor de verbetering ervan.</p>
                </section>
                
                <!-- Section 3 -->
                <section class="prose prose-lg max-w-none transition duration-700 p-2">
                    <h2 class="text-3xl font-medium text-zinc-900 mb-4">3. Intellectuele eigendom</h2>
                    <p>Alle intellectuele eigendomsrechten met betrekking tot onze diensten, waaronder maar niet beperkt tot software, ontwerpen, code, documenten en methodologieën, blijven eigendom van <span class="text-nowrap">c-o-d-e-s</span> tenzij schriftelijk anders overeengekomen.</p>
                    <p>Bij levering van custom software kunnen afspraken worden gemaakt over de overdracht van specifieke rechten aan de klant. Tenzij expliciet anders overeengekomen, verkrijgt de klant een niet-exclusieve, niet-overdraagbare licentie voor het gebruik van de geleverde software.</p>
                    <p>Het is niet toegestaan om:</p>
                    <ul>
                        <li>De broncode van onze software te decompileren, reverse-engineeren of wijzigen</li>
                        <li>Onze materialen te kopiëren, distribueren of publiceren zonder toestemming</li>
                        <li>Merken, logo's en andere eigendomsaanduidingen te verwijderen</li>
                    </ul>
                </section>
                
                <!-- Section 4 -->
                <section class="prose prose-lg max-w-none transition duration-700 p-2">
                    <h2 class="text-3xl font-medium text-zinc-900 mb-4">4. Gebruiksvoorwaarden</h2>
                    <p>Bij het gebruik van onze diensten en producten stem je ermee in:</p>
                    <ul>
                        <li>Geen activiteiten uit te voeren die de werking van onze diensten kunnen verstoren</li>
                        <li>Geen ongeautoriseerde toegang te verkrijgen tot onze systemen of netwerken</li>
                        <li>Geen materiaal te uploaden dat onwettig, frauduleus of schadelijk is</li>
                        <li>Onze diensten niet te gebruiken voor illegale doeleinden</li>
                        <li>Onze diensten niet te gebruiken op een manier die schade kan toebrengen aan anderen</li>
                    </ul>
                    <p>We behouden ons het recht voor om de toegang tot onze diensten te beperken of te beëindigen als er sprake is van schending van deze voorwaarden.</p>
                </section>
                
                <!-- Section 5 -->
                <section class="prose prose-lg max-w-none transition duration-700 p-2">
                    <h2 class="text-3xl font-medium text-zinc-900 mb-4">5. Beperking van aansprakelijkheid</h2>
                    <p>In de maximaal door de wet toegestane mate, is <span class="text-nowrap">c-o-d-e-s</span> niet aansprakelijk voor:</p>
                    <ul>
                        <li>Indirecte, incidentele, speciale of gevolgschade</li>
                        <li>Verlies van data, winst, inkomsten of kansen</li>
                        <li>Onderbrekingen in de beschikbaarheid van diensten</li>
                        <li>Schade als gevolg van het gebruik of onvermogen om gebruik te maken van onze diensten</li>
                    </ul>
                    <p>Onze totale aansprakelijkheid is in alle gevallen beperkt tot het bedrag dat door jou is betaald voor de diensten gedurende de zes maanden voorafgaand aan de claim.</p>
                </section>
                
                <!-- Section 6 -->
                <section class="prose prose-lg max-w-none transition duration-700 p-2">
                    <h2 class="text-3xl font-medium text-zinc-900 mb-4">6. Wijzigingen</h2>
                    <p>We behouden ons het recht voor om deze algemene voorwaarden op elk moment te wijzigen. Bij substantiële wijzigingen zullen we je hiervan op de hoogte stellen via e-mail of door een kennisgeving op onze website.</p>
                    <p>Voortgezet gebruik van onze diensten na zulke wijzigingen betekent dat je de herziene voorwaarden accepteert.</p>
                </section>
                
                <!-- Section 7 -->
                <section class="prose prose-lg max-w-none transition duration-700 p-2">
                    <h2 class="text-3xl font-medium text-zinc-900 mb-4">7. Toepasselijk recht</h2>
                    <p>Deze algemene voorwaarden worden beheerst door Nederlands recht. Alle geschillen die voortvloeien uit of verband houden met deze voorwaarden zullen worden voorgelegd aan de bevoegde rechtbank in Nederland.</p>
                    <p>Voor vragen of opmerkingen over deze algemene voorwaarden kunt u contact met ons opnemen via:</p>
                    <p>
                        <span class="text-nowrap">c-o-d-e-s</span> software development<br>
                        Brugstraat 11<br>
                        7607 XJ Almelo<br>
                        Nederland<br>
                        E-mail: info@codes.nl
                    </p>
                    <p>Laatste update: {{ new Date().toLocaleDateString('nl-NL', {day: 'numeric', month: 'long', year: 'numeric'}) }}</p>
                </section>
                
                <div class="mt-12 flex justify-center">
                    <Button href="/" variant="white" icon="chevron-right" class="group text-base sm:text-sm">Terug naar home</Button>
                </div>
            </div>
        </div>
    </main>
</template>
